import { useCommentsContext } from "context/CommentsContext";

// Services & Utils
import { getThreadsBy } from "services/comments";

import {
  addCommentToMap,
  addNewCommentToMap,
  loadAllCommentsToMap,
  removeAllCommentsFromMap,
  removeAllSelectedThreadMarks,
  removeCommentFromMap,
  setSelectedThreadMark,
  zoomToThread
} from "utils/OLMapUtils";

/**
 * Hook to manage threads from the threads context.
 *
 * @returns Object with all the public methods
 */
const useThreads = () => {
  const [commentsState, commentsDispatch] = useCommentsContext();

  /**
   * Method to reset the comments context
   * to the initial state.
   */
  const commentsContextCleanup = () => {
    commentsDispatch({
      type: "CLEANUP"
    });
  };

  /**
   * Method retrieve all the theads.
   */
  const retrieveThreads = async (projectId = null, scenarioId = null) => {
    if (projectId) {
      commentsDispatch({
        type: "FETCHING_START"
      });
      getThreadsBy(projectId, scenarioId)
        .then(res => {
          commentsDispatch({
            type: "THREADS_FETCH_SUCCESS",
            payload: { threads: res }
          });
        })
        .catch(err => {
          commentsDispatch({
            type: "THREADS_FETCH_FAIL",
            payload: { error: err }
          });
        });
    }
  };

  /**
   * Sets the currently selected thread. If a map is
   * passed as a parameter, it will clear all threads
   * from the map and add a selected mark to the new
   * selected thread.
   */
  const setSelectedThreadId = (map, value) => {
    if (map) {
      clearAllSelectedThreads(map);
      setSelectedThreadMark(map, value, true);
    }
    commentsDispatch({
      type: "THREAD_SELECTED_SET",
      payload: { selectedThreadId: value }
    });
  };

  /**
   * Clears all selected marks from all threads and
   * sets the selectedThreadId to null.
   *
   * @param {object} map
   */
  const clearAllSelectedThreads = map => {
    removeAllSelectedThreadMarks(map);
    commentsDispatch({
      type: "THREAD_SELECTED_CLEAR"
    });
  };

  /**
   * Show all threads from the context on the map.
   *
   * @param {object} map
   * @param {string} viewType
   */
  const showAllThreadsOnMap = (map, viewType) => {
    if (viewType === "2D") {
      loadAllCommentsToMap(map, commentsState.threads);
      commentsDispatch({
        type: "THREADS_RENDERED_SET",
        payload: { onMap: true }
      });
    }
  };

  /**
   * Adds an aleady exsting thread to the map given
   * the coordinates, the color and the thread id,
   * project id and scenario id.
   *
   * @param {object} map
   * @param {string} viewType
   * @param {array} coordinates
   * @param {number} threadId
   * @param {string} color
   * @param {number} projectId
   * @param {number} scenarioId
   */
  const addNewThreadToMap = (
    map,
    viewType,
    coordinates,
    threadId,
    color,
    projectId,
    scenarioId
  ) => {
    if (viewType === "2D") {
      addNewCommentToMap(
        map,
        coordinates,
        threadId,
        color,
        scenarioId,
        projectId
      );
    }
  };

  /**
   * Adds an exsting thread to the map given the
   * coordinates, the color and the thread id,
   * project id and scenario id.
   *
   * @param {object} map
   * @param {string} viewType
   * @param {array} coordinates
   * @param {number} threadId
   * @param {string} color
   * @param {number} projectId
   * @param {number} scenarioId
   */
  const addThreadToMap = (
    map,
    viewType,
    coordinates,
    threadId,
    color,
    projectId,
    scenarioId
  ) => {
    if (viewType === "2D") {
      addCommentToMap(map, coordinates, threadId, color, scenarioId, projectId);
    }
  };

  /**
   * Removes a thread from the map by thread id.
   *
   * @param {object} map
   * @param {string} viewType
   * @param {number} threadId
   */
  const removeThreadFromMap = (map, viewType, threadId) => {
    if (viewType === "2D") {
      removeCommentFromMap(map, threadId);
    }
  };

  /**
   * Removes all threads from the map.
   *
   * @param {object} map
   * @param {string} viewType
   */
  const removeAllThreadsFromMap = (map, viewType) => {
    if (viewType === "2D") {
      removeAllCommentsFromMap(map);
      commentsDispatch({
        type: "THREADS_RENDERED_SET",
        payload: { onMap: false }
      });
    }
  };

  /**
   * Zooms to the point on the map of the selected thread.
   *
   * @param {object} map
   * @param {number} threadId
   */
  const zoomToSelectedThread = (map, threadId) => {
    zoomToThread(map, threadId);
  };

  /**
   * Sets the flag 'refreshComments' indicating
   * that the comment list must be re-fetched.
   * @param {boolean} value
   */
  const setRefreshComments = value => {
    commentsDispatch({
      type: "COMMENTS_REFRESH_SET",
      payload: { refreshComments: value }
    });
  };

  /**
   * Sets the flag 'newCommentsAvailable' indicating
   * that there are new comments not in the current
   * comment list.
   *
   * @param {boolean} value
   */
  const setNewCommentsAvailable = value => {
    commentsDispatch({
      type: "COMMENTS_NEW_AVAILABLE_SET",
      payload: { newCommentsAvailable: value }
    });
  };

  /**
   * Method to set the mode for the comment system:
   *  - scenario
   *  - project
   *  - onlymycomments
   */
  const setThreadsMode = mode => {
    commentsDispatch({
      type: "MODE_SET",
      payload: { mode: mode }
    });
  };

  /**
   * Method to set the context error state to null.
   */
  const resetErrors = () => {
    commentsDispatch({
      type: "ERRORS_RESET"
    });
  };

  return {
    commentsContextCleanup: commentsContextCleanup, // Resets the context state to its initial state.
    retrieveThreads: retrieveThreads, // Fetches all threads from back-end and stores them in the context.
    setSelectedThreadId: setSelectedThreadId, // Sets the selected thread to show on dialog.
    clearAllSelectedThreads: clearAllSelectedThreads, // Clears all selected marks from all threads.
    showAllThreadsOnMap: showAllThreadsOnMap, // Show all threads from the context on the map.
    addNewThreadToMap: addNewThreadToMap, // Adds a new thread to the map.
    addThreadToMap: addThreadToMap, // Adds an exsting thread to the map.
    removeThreadFromMap: removeThreadFromMap, // Removes a thread from the map.
    removeAllThreadsFromMap: removeAllThreadsFromMap, // Removes all threads from the map.
    zoomToSelectedThread: zoomToSelectedThread, // Zooms to the point of the selected thread.
    setRefreshComments: setRefreshComments, // If set, fetches all comments from back-end and stores them in the context.
    setNewCommentsAvailable: setNewCommentsAvailable, // Sets a flag to warn that new comments are available.
    setThreadsMode: setThreadsMode, // Sets the mode of retrieving threads/comments: by project/scenaio/owner
    resetErrors: resetErrors // Resets the error in the state.
  };
};

export default useThreads;
